<template>
  <div :id="wrapperId" class="nation-map-wrapper">
    <loading class="map-loading-overlay" v-if="loading" color="#333" :loading="loading" text="Loading Models"></loading>
    <div :id="id" class="nationwide-map">
      <!-- <v-btn id="zoom-btn" @click="zoomEnabled = !zoomEnabled">
        <span v-if="zoomEnabled">Disable Zoom</span>
        <span v-if="!zoomEnabled">Enable Zoom</span>
      </v-btn>
      <v-btn id="reset-btn" :disabled="!zoomEnabled">Reset</v-btn> -->
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as d3 from 'd3'
import * as topojson from 'topojson'

import Utils from '@/utils'
import Loading from '@/components/common/Loading'

const name = 'nationwide_map'

const props = {
  id: {
    type: String,
    required: true
  },
  data: {
    type: Array,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  filterState: {
    type: String,
    required: false
  },
  loading: {
    type: Boolean,
    required: false
  }
}

const components = {
  Loading
}

const computed = {
  ...mapGetters({
    cds: 'geojson/cds',
    states: 'geojson/states',
    mobile: 'ux/mobile',
    screenWidth: 'ux/screenWidth'
  }),
  wrapperId () {
    return `${this.id}-wrapper`
  }
}

const methods = {
  getGradient (perc) {
    return `linear-gradient(to right,
      #66ABCF 0%,
      #66ABCF ${perc}%,
      #FA3138 ${perc}%,
      #FA3138 100%)`
  },
  getObject (d) {
    return this.data.find(item => {
      if (item.election && this.type === 'house') {
        return item.election.geoid === d.properties.GEOID
      }
      if (item.election && this.type === 'senate') {
        if (item.election.specialElection === true) {
          return item.election.state + ' (Special)' === d.properties.NAME
        }
        return item.election.state === d.properties.NAME
      }
    })
  },
  getCandidates (obj) {
    const electionJson = JSON.parse(obj.election.electionJson)
    const candidates = Object.values(electionJson.current_election)
    const dems = candidates.filter(item => item.party.includes('Democratic'))
    const reps = candidates.filter(item => item.party.includes('Republican') || item.party.includes('Conservative'))
    if (dems.length <= 1 || reps.length <= 1) {
      return [...dems, ...reps]
    } else {
      return 'Primary has not been decided yet'
    }
  },
  getDistrictState (value) {
    const obj = Utils.getColorScale(value)
    return `<span style="background-color: ${obj.color}; color: ${obj.textColor}; padding: 5px 10px;">${obj.state}</span>`
  },
  getPercentage (v) {
    let perc = v
    if (perc > 99) {
      perc = '> ' + 99
    } else if (perc < 1) {
      perc = '< ' + 1
    }
    return perc
  },
  drawMap () {
    const map = d3.select(`#${this.id}`)
    map.selectAll('svg').remove()
    const legends = d3.select('#map-legend-wrapper')
    legends.selectAll('*').remove()

    let width = map._groups[0][0].clientWidth
    let height = map._groups[0][0].clientHeight
    // let active = d3.select(null)

    const cds = JSON.parse(this.cds)
    const states = JSON.parse(this.states)

    let geojson = null
    if (this.type === 'house') {
      geojson = topojson.feature(cds, cds.objects.cd)
    } else if (this.type === 'senate') {
      geojson = topojson.feature(states, states.objects.state)
    }

    const svg = d3.select(`#${this.id}`).append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('position', 'absolute')
      .on('click', () => {
        if (!this.mobile) {
          this.tooltip.style('display', 'none')
        }
        if (d3.event.defaultPrevented) d3.event.stopPropagation()
      }, true)

    if (this.filterState && this.filterState !== '00') {
      geojson.features = geojson.features.filter(item => {
        return item.properties.STATEFP === this.filterState
      })
    }

    let projection = null
    if (this.filterState && this.filterState !== '00') {
      projection = d3.geoMercator().fitSize([width, (height - 30)], geojson)
    } else {
      projection = d3.geoAlbersUsa().fitSize([width, (height - 30)], geojson)
    }
    let path = d3.geoPath().projection(projection)

    const g = svg.append('g')

    // if (this.zoomEnabled) {
    //   const zoom = d3.zoom()
    //     .scaleExtent([1, 8])
    //     .on('zoom', () => {
    //       tooltip.style('display', 'none')
    //       g.style('stroke-width', () => {
    //         if (this.type === 'house') {
    //           return 0.1 / d3.event.transform.k + 'px'
    //         } else {
    //           return 2 / d3.event.transform.k + 'px'
    //         }
    //       })
    //       g.attr('transform', d3.event.transform)
    //     })

    //   map.call(zoom)
    // }

    this.tooltip = d3.select('.application--wrap').append('div')
      .attr('class', 'map-tooltip')
      .on('click', () => {
        if (this.mobile && d3.event.target.className === 'tooltip-close material-icons') {
          this.tooltip.style('display', 'none')
        }
      })
      .style('display', 'none')

    const mapLegendWrapper = d3.select('#map-legend-wrapper')
      .attr('class', 'map-legend-wrapper')

    mapLegendWrapper.append('text')
      .attr('class', 'legend-title')
      .text(() => { return 'GOP Win Probability' })

    const boxWidth = document.getElementById('map-legend-wrapper').clientWidth
    const legendSpacing = (scale, i) => {
      const arr = scale.slice(0, i)
      if (arr.length > 0) {
        const widths = arr.map(item => (item.width / 100) * boxWidth)
        return widths.reduce((a, b) => a + b)
      } else {
        return 0
      }
    }

    Utils.colorScale.forEach((item, i) => {
      let index = i + 1
      if (i < Utils.colorScale.length - 1) {
        mapLegendWrapper.append('text')
          .attr('class', 'legend-value')
          .style('left', legendSpacing(Utils.colorScale, index) - 8 + 'px')
          .text(() => { return item.upper + '%' })
      }
    })

    const mapLegend = mapLegendWrapper.append('svg')
      .attr('class', 'map-legend')
      .attr('width', '100%')
      .attr('height', 20)
      .selectAll('g')
      .data(Utils.colorScale.map(item => item.color))
      .enter()
      .append('g')
      .attr('transform', (d, i) => { return 'translate(' + legendSpacing(Utils.colorScale, i) + ', 0)' })

    mapLegend.append('rect')
      .attr('width', (d, i) => { return (Utils.colorScale[i].width / 100) * boxWidth })
      .attr('height', 20)
      .style('fill', (d, i) => { return Utils.colorScale[i].color })

    g.selectAll('path')
      .data(geojson.features)
      .enter().append('path')
      .attr('class', 'cd')
      .attr('d', path)
      .style('fill', (d) => {
        const obj = this.getObject(d)
        if (obj) {
          const scale = Utils.getColorScale(obj.gopWinProbability * 100)
          return obj ? scale.color : false
        } else {
          return 'rgba(102, 102, 102, 0.3)'
        }
      })
      .style('opacity', 0.9)
      .style('stroke-width', () => {
        if (this.type === 'house') {
          return '0.2px'
        } else {
          return '0.6px'
        }
      })
      .on('mouseover', (d) => {
        if (!this.mobile) {
          this.tooltip.text('')
            .style('display', 'inline')
            .style('left', () => {
              return (d3.event.pageX - 300) + 'px'
            })
            .style('top', (d3.event.pageY - 150) + 'px')
        } else {
          this.tooltip.text('')
            .style('display', 'block')
        }

        const content = this.tooltip.append('div').attr('class', 'map-tooltip-content')
        content.html(() => {
          const obj = this.getObject(d)
          let candidatesContent = null
          if (typeof obj !== 'undefined') {
            const candidates = this.getCandidates(obj)
            let dem = null
            let rep = null
            if (typeof candidates === 'string') {
              candidatesContent = `<div style="font-weight: 600; margin-bottom: 5px;">Primary has not occurred yet</div>`
            } else {
              dem = candidates.filter(item => item.party.includes('Democratic'))
              if (dem.length === 1 && dem[0].candidate_name) {
                dem = dem[0].candidate_name
                dem = dem.split(' ')[1]
              } else if (dem.length > 1) {
                dem = 'MULTIPLE'
              } else {
                dem = 'NONE'
              }
              rep = candidates.filter(item => item.party.includes('Republican') || item.party.includes('Conservative'))
              if (rep.length === 1 && rep[0].candidate_name) {
                rep = rep[0].candidate_name
                rep = rep.split(' ')[1]
              } else if (rep.length > 1) {
                rep = 'MULTIPLE'
              } else {
                rep = 'NONE'
              }
              candidatesContent = `
                <div class="content-candidates">
                  <span style="color: #66ABCF; text-align: left;">${dem}</span>
                  <span style="color: #FA3138; text-align: right;">${rep}</span>
                </div>
              `
            }
          }
          let name = null
          if (obj && this.type === 'house') {
            name = `${obj.election.state}: ${obj.election.cd}`
          } else if (obj && this.type === 'senate') {
            if (obj.election.specialElection) {
              name = `${obj.election.state} (Special)`
            } else {
              name = `${obj.election.state}`
            }
          }
          if (obj) {
            let header = ''
            if (this.mobile) {
              header = `
                <div class="content-title">
                ${name}
                <i class="tooltip-close material-icons" style="color: #333; font-size: 1.8em;">close</i>
                </div>
              `
            }
            const flip = Utils.getFlip(obj.potentialFlip)
            return `
              ${header}
              <div class="content-party">
                <span class="icon-democrat" style="font-size: 3em; color: #66ABCF;"></span>
                <span class="content-state">${this.getDistrictState((obj.gopWinProbability * 100).toFixed(1))}</span>
                <span class="icon-republican" style="font-size: 3em; color: #FA3138"></span>
              </div>
              <div class="content-perc">
                <span>${this.getPercentage((100 - obj.gopWinProbability * 100).toFixed(1))}%</span>
                <span>${this.getPercentage((obj.gopWinProbability * 100).toFixed(1))}%</span>
              </div>
              ${candidatesContent}
              <div class="content-bar" style="background: ${this.getGradient((100 - obj.gopWinProbability * 100).toFixed(1))}"></div>
              <div class="content-flip" v-if="${flip}">
                <span>${flip ? flip.text : ''}</span>
              </div>
            `
          } else {
            return '<div class="content-title">No Elections in this Geography</div>'
          }
        })
      })
      .on('mousemove', (d) => {
        if (!this.mobile) {
          const height = this.tooltip._groups[0][0].clientHeight + 12
          this.tooltip
            .style('left', () => {
              const pageWidth = document.body.clientWidth
              if (pageWidth - d3.event.pageX > 200) {
                return (d3.event.pageX - 100) + 'px'
              } else {
                return (d3.event.pageX - 200) + 'px'
              }
            })
            .style('top', (d3.event.pageY - height) + 'px')
        }
      })
      .on('mouseout', (d) => {
        this.tooltip.style('display', 'none')
      })
      .on('click', (d) => {
        if (!this.mobile) {
          this.tooltip.style('display', 'none')
          if (this.type === 'senate') {
            const item = this.data.find(item => item.election.state === d.properties.NAME)
            this.$router.push({ name: 'senate', params: { raceId: item.election.raceId } })
          }
          if (this.type === 'house') {
            const item = this.data.find(item => item.election.geoid === d.properties.GEOID)
            this.$router.push({ name: 'house', params: { raceId: item.election.raceId } })
          }
        }
      })

    // const resetBtn = d3.select('#reset-btn')
    // resetBtn.on('click', () => {
    //   map.call(zoom.transform, d3.zoomIdentity)
    // })
  }
}

export default {
  name,
  props,
  components,
  computed,
  methods,
  data () {
    return {
      blues: ['#dde9f0', '#c2d9e8', '#a5c9df', '#88bad7', '#66abcf'],
      reds: ['#fae4e4', '#ffbeb7', '#fe978d', '#f96d64', '#ef383d']
      // zoomEnabled: false
    }
  },
  mounted () {
    this.drawMap()
  },
  watch: {
    data () {
      this.drawMap()
    },
    filterState () {
      this.drawMap()
    },
    screenWidth () {
      this.drawMap()
    },
    zoomEnabled () {
      this.drawMap()
    }
  },
  beforeDestroy () {
    const els = document.querySelectorAll('.map-tooltip')
    els.forEach(el => el.remove())
  }
}
</script>

<style>
.nation-map-wrapper {
  position: relative;
  width: 100%;
}

.nationwide-map {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 500px;
  cursor: pointer;
}

.cd {
  fill: #9E9E9E;
  stroke: #fff;
}
path:hover {
  fill-opacity: 0.7;
}

.map-tooltip {
  position: absolute;
  text-align: center;
  width: 220px;
  max-height: 130px;
  padding: 10px;
  font: 12px sans-serif;
  background: white;
  border: 1px solid #222;
  pointer-events: none;
  z-index: 2000;
}

.map-tooltip-content {
  width: 100%;
  max-height: 130px;
}

.content-title {
  text-align: center;
  font-size: 1.2em;
  font-weight: 700;
  margin-bottom: 10px;
}

.content-party {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.content-state {
  font-size: 1.2em;
  font-weight: 600;
  margin: 0 10px;
  text-transform: uppercase;
}

.content-perc {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: space-between;
  font-size: 1.4em;
  font-weight: 600;
  margin-bottom: 5px;
}

.content-candidates {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  letter-spacing: 0.08em;
  font-size: 1em;
}

.content-flip {
  position: relative;
  bottom: 23px;
  color: white;
  text-transform: uppercase;
  font-size: 1em;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.content-bar {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  height: 24px;
  widows: 100%;
  margin-bottom: 5px;
}

.map-legend {
  position: absolute;
  margin-top: 30px;
  border: 1px solid black;
}

.legend-value {
  position: absolute;
  top: 50px;
  font-size: 12px;
  letter-spacing: 0.05em;
}

.legend-title {
  position: absolute;
  width: 100%;
  text-align: center;
  font-weight: 600;
  letter-spacing: 0.05em;
}

#reset-btn {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 150;
}

#zoom-btn {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 150;
}

.map-loading-overlay {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

.map-loading-overlay p {
  font-weight: 600;
  color: #333;
  letter-spacing: 0.05em;
  padding: 0 5px;
}

@media only screen and (max-width: 600px) {
  .nationwide-map {
    height: 400px;
  }
}

@media only screen and (max-width: 400px) {
  .nationwide-map {
    height: 300px;
  }
}

@media only screen and (max-width: 960px) {
  body {
    cursor: pointer;
  }

  .map-tooltip {
    position: fixed;
    bottom: 0px;
    min-width: 100%;
    max-width: 100%;
    max-height: 180px;
    z-index: 999;
    border: none;
    border-top: 1px solid #222;
    min-height: 80px;
    box-shadow: 0px -4px 3px rgba(0, 0, 0, 0.1);
    pointer-events: all;
    cursor: pointer;
  }

  .map-tooltip-content {
    width: 100%;
    max-height: 140px;
  }

  .content-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  #tooltip-close {
    cursor: pointer;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/charts/NationwideMap.vue